import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, Filter } from 'lucide-react';
import { format, addMonths } from 'date-fns';

const COST_TYPE_OPTIONS = [
  { value: 'all', label: 'All Types' },
  { value: 'fuel', label: 'Fuel' },
  { value: 'service', label: 'Service' },
  { value: 'tyres', label: 'Tyres' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'labour', label: 'Labour' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'other', label: 'Other' },
];

/**
 * Reports filter/date-picker bar: month navigator + vehicle & cost-type filters.
 * Pure presentation component — parent owns the state.
 */
export const ReportsFilterBar = ({
  selectedMonth,
  setSelectedMonth,
  monthOptions,
  prevMonth,
  nextMonth,
  vehicleFilter,
  setVehicleFilter,
  vehicles,
  costTypeFilter,
  setCostTypeFilter,
}) => {
  const isCurrentOrFuture =
    format(addMonths(new Date(selectedMonth + '-01'), 1), 'yyyy-MM') > format(new Date(), 'yyyy-MM');
  const hasActiveFilters = vehicleFilter !== 'all' || costTypeFilter !== 'all';

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex flex-wrap items-center gap-4">
          {/* Month Navigator */}
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={prevMonth}
              data-testid="prev-month-btn"
              className="h-9 w-9"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger className="w-[200px] font-medium" data-testid="month-select">
                <CalendarIcon className="w-4 h-4 mr-2 text-slate-400" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {monthOptions.map((m) => (
                  <SelectItem key={m.value} value={m.value}>
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="ghost"
              size="icon"
              onClick={nextMonth}
              data-testid="next-month-btn"
              className="h-9 w-9"
              disabled={isCurrentOrFuture}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>

          <div className="h-6 w-px bg-slate-200" />

          {/* Vehicle + Cost Type Filters */}
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-slate-400" />
            <Select value={vehicleFilter} onValueChange={setVehicleFilter}>
              <SelectTrigger className="w-[180px]" data-testid="vehicle-filter-select">
                <SelectValue placeholder="All Vehicles" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Vehicles</SelectItem>
                {vehicles.map((v) => (
                  <SelectItem key={v.id} value={v.id}>
                    {v.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={costTypeFilter} onValueChange={setCostTypeFilter}>
              <SelectTrigger className="w-[150px]" data-testid="cost-type-filter-select">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                {COST_TYPE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setVehicleFilter('all');
                setCostTypeFilter('all');
              }}
              data-testid="clear-filters-btn"
            >
              Clear filters
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
